import React, { useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import MDBox from "shared/ui/Box/MDBox";
import MDTypography from "shared/ui/Typography/MDTypography";
import PlannerGrid from "features/planner/components/planner/PlannerGrid";
import NotesEditor from "features/planner/components/notes/NotesEditor";
import useDeviceType from "hooks/useDeviceType";
import useViewportInfo from "hooks/useViewportInfo";
import CategoryBar from "features/planner/components/category/CategoryBar";
import ExerciseList from "features/planner/components/exercise/ExerciseList";
import ViewModeSelector from "features/planner/components/actions/ViewModeSelector";
import DownloadPdfButton from "features/planner/components/actions/DownloadPdfButton";
import PreviewPdfButton from "features/planner/components/actions/PreviewPdfButton";
import ClearMenu from "features/planner/components/actions/ClearMenu";
import ProgramSettings from "features/planner/components/notes/ProgramSettings";
import ViewportTooSmall from "features/planner/components/planner/ViewportTooSmall";
import ExerciseSearch from "features/planner/components/exercise/ExerciseSearch";
import CategorySearch from "features/planner/components/category/CategorySearch";
import ExerciseEditModal from "features/planner/components/planner/ExerciseEditModal";
import CurrentProgramCard from "features/planner/components/actions/CurrentProgramCard";
import { DndContext, DragOverlay } from "@dnd-kit/core";
import GhostExerciseSlotItem from "features/planner/components/ghosts/GhostExerciseSlotItem";
import GhostExerciseCard from "features/planner/components/ghosts/GhostExerciseCard";
import EditModeButton from "features/planner/components/actions/EditModeButton";
import EditModeActionsPanel from "features/planner/components/editmode/EditModeActionsPanel";
import useExerciseCatalog from "features/planner/hooks/useExerciseCatalog";
import colors from "app/theme/base/colors";
import { alpha } from "@mui/material/styles";

import usePlanner from "features/planner/state/usePlanner";
import SaveButton from "features/planner/components/actions/SaveButton";
import UnsavedChangesGuard from "features/planner/components/navigation/UnsavedChangesGuard";
import { useParams } from "react-router-dom";

function PlannerPage() {
  const { programId } = useParams();
  const showViewportDebug = process.env.REACT_APP_VIEWPORT_DEBUG === "true";

  const { isLarge } = useDeviceType();
  const { width, height, type } = useViewportInfo("Planner viewport");

  const [viewMode, setViewMode] = useState("planner");

  React.useEffect(() => {
    setViewMode("planner");
  }, [programId]);

  const [activeItem, setActiveItem] = useState(null);
  const {
    exercises,
    categories,
    activeCategories,
    toggleCategory,
    isCategoriesLoading,
    isCatalogLoading,
  } = useExerciseCatalog();
  const [searchText, setSearchText] = useState("");
  const filteredExercises = searchText.trim()
    ? exercises.filter((ex) => ex.name.toLowerCase().includes(searchText.toLowerCase()))
    : exercises;

  const [categorySearch, setCategorySearch] = useState("");
  const filteredCategories = categorySearch.trim()
    ? categories.filter((c) => c.name.toLowerCase().includes(categorySearch.toLowerCase()))
    : categories;

  const {
    programState,
    placeExercise,
    moveExercise,
    swapExercises,
    updateExercise,
    isEditMode,
    enterEditMode,
    exitEditMode,
    hasUnsavedChanges,
  } = usePlanner();

  // ---------------------------
  // 🔥 Estado del modal/popover
  // ---------------------------
  const [editing, setEditing] = useState({
    open: false,
    day: null,
    slotIndex: null,
    anchorEl: null,
  });

  // Cerrar modal al cambiar de vista
  React.useEffect(() => {
    setEditing((currentEditing) => {
      if (!currentEditing.open) return currentEditing;

      return {
        open: false,
        day: null,
        slotIndex: null,
        anchorEl: null,
      };
    });
  }, [viewMode]);

  if (width < 1200 || height < 630) {
    return <ViewportTooSmall />;
  }

  const handleEditSlot = (day, index, slotEl) => {
    setEditing({
      open: true,
      day,
      slotIndex: index,
      anchorEl: slotEl,
    });
  };

  const handleCloseModal = () => {
    setEditing({
      open: false,
      day: null,
      slotIndex: null,
      anchorEl: null,
    });
  };

  const handleSaveModal = (fields) => {
    if (!editing.day) return;

    updateExercise(editing.day, editing.slotIndex, fields);
    handleCloseModal();
  };

  const handleEnterEditMode = () => {
    setViewMode("planner");
    enterEditMode();
  };

  const handleExitEditMode = () => {
    exitEditMode();
  };

  const handleInvalidProgramNavigation = () => {
    if (isEditMode) {
      handleExitEditMode();
    }
    setViewMode("notes");
  };

  return (
    <>
      <UnsavedChangesGuard onInvalidProgram={handleInvalidProgramNavigation} />

      <style>{`
    *:not(input):not(textarea):not([contenteditable="true"]) {
      user-select: none !important;
      -webkit-user-select: none !important;
      -ms-user-select: none !important;
    }
  `}</style>

      {activeItem && (
        <style>{`
    .slot-overlay {
      pointer-events: none !important;
      opacity: 0 !important;
    }
  `}</style>
      )}

      {activeItem && (
        <style>{`
    body, body * {
      cursor: grabbing !important;
    }
  `}</style>
      )}

      <MDBox
        sx={{
          position: isEditMode ? "fixed" : "relative",
          inset: isEditMode ? 0 : "auto",
          zIndex: isEditMode ? (theme) => theme.zIndex.modal + 1 : "auto",
          width: isEditMode ? "100vw" : "100%",
          paddingLeft: isEditMode ? "12px" : 0,
          paddingRight: isEditMode ? "12px" : 0,
          paddingTop: isEditMode ? "8px" : 0,
          paddingBottom: isEditMode ? "4px" : 0,
          borderRadius: isEditMode ? 0 : "12px",
          boxShadow: "none",
          height: isEditMode ? "100vh" : "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          backgroundColor: isEditMode ? "background.default" : "transparent",
        }}
      >
        {/* Header de categorías */}
        {!isEditMode && (
          <MDBox
            sx={{
              width: "100%",
              backgroundColor: "background.paper",
              borderRadius: "12px",
              padding: "14px 18px",
              boxShadow: "none",
              border: "1px solid",
              borderColor: "grey.300",
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              mb: 1.2,
            }}
          >
            {/* Solo el buscador — actúa como "título" */}
            <CategorySearch
              value={categorySearch}
              onChange={setCategorySearch}
              sx={{ ml: "4px" }}
            />

            {/* Barra filtrada */}
            {isCategoriesLoading ? (
              <MDBox display="flex" alignItems="center" justifyContent="center" gap={1} py={0.5}>
                <CircularProgress size={18} />
                <MDTypography variant="caption" color="text">
                  Cargando categorías...
                </MDTypography>
              </MDBox>
            ) : (
              <CategoryBar
                categories={filteredCategories}
                activeCategories={activeCategories}
                toggleCategory={toggleCategory}
              />
            )}
          </MDBox>
        )}
        {/* DND CONTEXT */}
        <DndContext
          onDragStart={({ active }) => {
            document.body.classList.add("dnd-dragging");
            const data = active?.data?.current;
            if (!data) return;

            if (data.type === "exercise-card" && viewMode !== "planner") {
              setViewMode("planner");
            }

            setActiveItem(data);
          }}
          onDragEnd={({ active, over }) => {
            document.body.classList.remove("dnd-dragging");
            setActiveItem(null);

            if (!active || !over) return;

            const activeData = active.data.current;
            const overData = over.data.current;

            if (!activeData || !overData) return;
            if (overData.type !== "slot") return;

            const { day: overDay, index: overIndex } = overData;

            // Desde lista
            if (activeData.type === "exercise-card") {
              placeExercise(overDay, overIndex, activeData.exercise);
              return;
            }

            // Entre slots
            if (activeData.type === "slot-item") {
              const { day: fromDay, index: fromIndex } = activeData;

              const originSlot = programState.days[fromDay]?.slots[fromIndex];
              const targetSlot = programState.days[overDay]?.slots[overIndex];

              if (!originSlot) return;

              // A) Slot destino vacío
              if (!targetSlot) {
                moveExercise(fromDay, fromIndex, overDay, overIndex);
                return;
              }

              // B) Intercambio
              swapExercises(fromDay, fromIndex, overDay, overIndex);

              return;
            }
          }}
          onDragCancel={() => {
            document.body.classList.remove("dnd-dragging");
            setActiveItem(null);
          }}
        >
          {/* Secciones principales */}
          <Grid
            container
            spacing={isLarge ? 1.6 : 1}
            sx={{ flexGrow: 1, minHeight: 0, alignItems: "stretch" }}
          >
            {/* Lista ejercicios */}
            {!isEditMode && (
              <Grid item xs={12} md={1.5} sx={{ height: "100%" }}>
                <Card
                  sx={{
                    p: 2,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    backgroundColor: "background.paper",
                    border: "1px solid",
                    borderColor: "grey.300",
                  }}
                >
                  <ExerciseSearch value={searchText} onChange={setSearchText} />

                  {isCatalogLoading ? (
                    <MDBox
                      sx={{
                        flexGrow: 1,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1,
                      }}
                    >
                      <CircularProgress size={22} />
                      <MDTypography variant="caption" color="text">
                        Cargando biblioteca de ejercicios...
                      </MDTypography>
                    </MDBox>
                  ) : (
                    <ExerciseList exercises={filteredExercises} />
                  )}
                </Card>
              </Grid>
            )}
            {/* Planner o Notes */}
            <Grid item xs={12} md={isEditMode ? 10 : 8.5} sx={{ height: "100%" }}>
              <Card
                sx={{
                  p: 1,
                  height: "100%",
                  overflowY: "auto",
                  overflowX: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  backgroundColor: "background.paper",
                  border: "1px solid",
                  borderColor: "grey.300",
                }}
              >
                {viewMode === "planner" ? (
                  <PlannerGrid activeItem={activeItem} onEditSlot={handleEditSlot} />
                ) : (
                  <MDBox
                    sx={{
                      flexGrow: 1,
                      minHeight: 0,
                      overflowY: "auto",
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                      pr: 1,
                    }}
                  >
                    <ProgramSettings />
                    <NotesEditor />
                  </MDBox>
                )}
              </Card>
            </Grid>

            {/* Acciones */}
            <Grid item xs={12} md={2} sx={{ height: "100%" }}>
              <Card
                sx={{
                  p: 2,
                  height: "100%",
                  overflowY: "auto",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 2,
                  backgroundColor: "background.paper",
                  border: "1px solid",
                  borderColor: "grey.300",
                }}
              >
                {!isEditMode ? (
                  <>
                    <CurrentProgramCard
                      title={programState.title}
                      clientName={programState.clientName}
                      hasUnsavedChanges={hasUnsavedChanges}
                    />

                    <ViewModeSelector viewMode={viewMode} setViewMode={setViewMode} />

                    <EditModeButton onClick={handleEnterEditMode} />

                    <MDBox sx={{ flexGrow: 1 }} />

                    <SaveButton />
                    <ClearMenu />
                    <PreviewPdfButton />
                    <DownloadPdfButton />
                  </>
                ) : (
                  <EditModeActionsPanel />
                )}
              </Card>
            </Grid>
          </Grid>

          {/* GHOST */}
          <div style={{ zIndex: 9999, position: "relative" }}>
            <DragOverlay dropAnimation={null}>
              {activeItem?.type === "slot-item" && (
                <GhostExerciseSlotItem
                  exercise={activeItem.exercise}
                  fontSize="0.8rem"
                  maxLines={2}
                />
              )}
              {activeItem?.type === "exercise-card" && (
                <GhostExerciseCard exercise={activeItem.exercise} />
              )}
            </DragOverlay>
          </div>
        </DndContext>

        {/* 🔥 AQUÍ VA EL MODAL / POPOVER */}
        <ExerciseEditModal
          open={editing.open}
          anchorEl={editing.anchorEl}
          onClose={handleCloseModal}
          onSave={handleSaveModal}
          slot={editing.day ? programState.days[editing.day].slots[editing.slotIndex] : null}
        />

        {/* Debugger */}
        {showViewportDebug && (
          <div
            style={{
              position: "fixed",
              bottom: 8,
              right: 12,
              background: alpha(colors.black.main, 0.6),
              color: "white",
              fontSize: "12px",
              fontFamily: "monospace",
              padding: "4px 8px",
              borderRadius: "6px",
              zIndex: 2000,
            }}
          >
            <div>{`Viewport: ${width}x${height}`}</div>
            <div>{`Type: ${type}`}</div>
          </div>
        )}
      </MDBox>
    </>
  );
}

export default PlannerPage;
